/**
 * Project coverage: the screen where being wrong would matter most.
 *
 * Everything here follows from one idea: the number this product computes is
 * *how much of the head was captured*, in *the regions it can measure*, and
 * every element on the page has to keep both halves of that sentence intact.
 *
 * The specific commitments, and what each prevents:
 *
 * **The headline never says the capture is finished.** `sufficient` reads
 * "Ölçülen bölgelerde kapsama yeterli". The system measures five regions of a
 * head; the back of the skull is not one of them. "Kafa çekimi tamamlandı"
 * would assert something never checked.
 *
 * **The scope is stated on the page, not in a tooltip.** Measured regions and
 * unmeasured areas come from the API and are listed. A user should not have to
 * discover the limits of the analysis by noticing what is missing.
 *
 * **Kapsama and Güven are never mixed.** Two numbers, two words, both labelled
 * every time they appear. Neither is called Doğruluk.
 *
 * **Bilinmiyor is not drawn as a failure.** It gets the neutral tone, a dashed
 * outline, a striped meter and its own sentence. A region nobody could measure
 * is an open question; a region measured and found lacking is a problem. The
 * user's next action differs, so the display has to.
 *
 * **The overall verdict is explained as a minimum.** It is the weakest region,
 * not an average, and the page says so - otherwise a single Yetersiz ear
 * beside four Yeterli regions looks like a bug.
 */

import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { getProjectCoverage, runProjectCoverage } from "@/api/client";
import { ApiError } from "@/api/errors";
import { COVERAGE_REGIONS, type CoverageRegion, type ProjectCoverage } from "@/api/types";
import { Button, Callout, DataList, EmptyState, Loading, StatusBadge } from "@/components/ui";
import { ErrorState } from "@/components/ErrorState";
import {
  COVERAGE_REGION_LABELS,
  COVERAGE_SCOPE_NOTE,
  COVERAGE_STATUS_TERMS,
  METRIC_HELP,
  METRIC_LABELS,
  OVERALL_EXPLANATION,
  OVERALL_HEADLINE,
  OVERALL_IS_WEAKEST_REGION,
  regionsSufficientSummary,
  SUBJECT_TYPE_TERMS,
  unmeasuredAreaLabel,
} from "@/i18n/terms";
import { formatDateTime, formatScore } from "@/lib/format";
import { useAction, useAsync } from "@/lib/useAsync";
import { useProjectContext } from "@/features/projects/ProjectScreen";
import { paths } from "@/app/paths";
import { CoverageDiagram } from "./CoverageDiagram";
import { CoverageInspector } from "./CoverageInspector";
import { PageHeader } from "@/components/product";
import { RegionCard } from "./RegionCard";
import { CoverageIssues } from "./CoverageIssues";
import "./coverage.css";
import { RegionReviewLink } from "./RegionReviewLink";
import { SceneCoveragePanel } from "./SceneCoveragePanel";
import { ViewpointCoveragePanel } from "./ViewpointCoveragePanel";

export function ProjectCoverageTab() {
  const { project } = useProjectContext();
  const subject = SUBJECT_TYPE_TERMS[project.subject_type];
  const coverageSupported =
    project.capabilities?.semantic_coverage ?? subject.hasCoverageAnalysis;
  const [reloadKey, setReloadKey] = useState(0);

  const fetcher = useCallback(
    (signal: AbortSignal) => coverageSupported
      ? getProjectCoverage(project.id, {}, { signal })
      : Promise.reject(new Error("unsupported")),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reloadKey forces a refetch after a run
    [project.id, reloadKey, coverageSupported],
  );
  const { state } = useAsync(fetcher);

  const { run, busy, error: runError } = useAction(
    useCallback(() => runProjectCoverage(project.id), [project.id]),
  );

  const recompute = async () => {
    const result = await run();
    if (result) setReloadKey((key) => key + 1);
  };

  // A place with people in it has three truths and no combined percentage,
  // so it gets its own screen rather than the spatial one with a person-shaped
  // hole in it. Checked before `coverageSupported`, which is true for this
  // type - it runs the face pipeline per person - and would otherwise send it
  // to the single-subject head report.
  // The angle-review queue, on the two panels that never offered it. It
  // draws nothing when the queue is empty.
  if (project.subject_type === "human_in_environment") {
    return (
      <div className="stack">
        <RegionReviewLink projectId={project.id} />
        <SceneCoveragePanel projectId={project.id} />
      </div>
    );
  }

  if (!coverageSupported) {
    // No `RegionReviewLink` here: this panel already links to the queue twice,
    // each time beside the specific finding that sends somebody there.
    return <ViewpointCoveragePanel projectId={project.id} subjectType={project.subject_type as Exclude<typeof project.subject_type, "human_head">} />;
  }

  // A 404 is the normal "not computed yet" answer, not an error to apologise
  // for. It gets an invitation instead of a red box.
  const notComputed =
    state.status === "error" &&
    state.error instanceof ApiError &&
    state.error.kind === "not_found";

  return (
    <div className="stack">
      {/* One page header, not two. The recompute action belongs beside the
          title rather than in a second heading row above it. */}
      <PageHeader
        title="Değerlendirilen Bölgelerin Kapsaması"
        actions={
          <Button variant="primary" busy={busy} onClick={() => void recompute()}>
            {state.data ? "Yeniden hesapla" : "Kapsamayı hesapla"}
          </Button>
        }
      />

      <RegionReviewLink projectId={project.id} />

      <Callout>{COVERAGE_SCOPE_NOTE}</Callout>

      {runError && <ErrorState error={runError} compact />}

      {state.status === "loading" && state.data === null && <Loading label="Kapsama yükleniyor" />}

      {notComputed && (
        <EmptyState
          title="Kapsama henüz hesaplanmadı"
          description="Çekimlerin analizini tamamladıktan sonra proje kapsamasını hesaplayabilirsiniz."
        />
      )}

      {state.status === "error" && !notComputed && <ErrorState error={state.error} />}

      {state.data && <CoverageReport projectId={project.id} coverage={state.data} />}
    </div>
  );
}

function CoverageReport({ projectId, coverage }: { projectId: string; coverage: ProjectCoverage }) {
  /** Which region the inspector describes. Null until the user picks one. */
  const [selected, setSelected] = useState<CoverageRegion | null>(null);
  const { overall } = coverage;
  const term = COVERAGE_STATUS_TERMS[overall.status];

  const measured = overall.measured_regions ?? [];
  const measuredSet = new Set<CoverageRegion>(measured);
  // Regions the API did not report as measured. Listed as "ölçülmedi" rather
  // than silently omitted, so a five-region vocabulary stays visible even when
  // only two of them had evidence.
  const notMeasured = COVERAGE_REGIONS.filter((region) => !measuredSet.has(region));
  const regions = coverage.regions ?? {};
  const changes = coverage.changes ?? {};

  // The correction call to action: which regions still need capture. Only
  // regions that were actually measured and fell short - an unmeasured region
  // cannot be said to "need more capture" in any specific way.
  const needsMore = measured.filter((region) => {
    const status = regions[region]?.status;
    return status === "partial" || status === "insufficient";
  });

  // The count of regions that reached the target, which is what a person can
  // act on. A real capture showed a 92% front face beside an overall 0% and
  // read as a contradiction; "1 / 5 bolge yeterli" does not.
  const sufficientCount = COVERAGE_REGIONS.filter(
    (r) => regions[r]?.status === "sufficient",
  ).length;

  return (
    <div className="stack">
      {/*
        The signature composition from the approved design: region list, the
        schematic, and an inspector for whichever region is selected. It sits
        above the existing sections rather than replacing them - those carry
        the coverage semantics this product is most careful about, and they
        are covered by tests that must keep passing.
      */}
      <section className="coverage-stage">
        <ul className="coverage-stage__list">
          {COVERAGE_REGIONS.map((region) => {
            const data = regions[region] ?? null;
            const status = data?.status ?? null;
            const regionTerm = status ? COVERAGE_STATUS_TERMS[status] : null;
            return (
              <li key={region}>
                <button
                  type="button"
                  className={`coverage-stage__item${
                    selected === region ? " coverage-stage__item--active" : ""
                  }`}
                  aria-pressed={selected === region}
                  onClick={() => setSelected(region)}
                >
                  <span
                    className={`coverage-stage__dot coverage-stage__dot--${status ?? "absent"}`}
                    aria-hidden="true"
                  />
                  <span className="coverage-stage__name">
                    {COVERAGE_REGION_LABELS[region]}
                  </span>
                  <span className={`coverage-stage__status coverage-stage__status--${status ?? "absent"}`}>
                    {regionTerm ? regionTerm.label : "Ölçülmedi"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <CoverageDiagram
          statuses={Object.fromEntries(
            COVERAGE_REGIONS.flatMap((region) =>
              regions[region] ? [[region, regions[region]!.status]] : [],
            ),
          )}
          selected={selected}
          onSelect={setSelected}
        />

        <CoverageInspector
          region={selected}
          data={selected ? (regions[selected] ?? null) : null}
          projectId={projectId}
        />
      </section>

      <section className={`card coverage-overall coverage-overall--${term.tone}`}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "start" }}>
          <div className="stack-tight">
            <p className="coverage-overall__count">
              {regionsSufficientSummary(sufficientCount, COVERAGE_REGIONS.length)}
            </p>
            <h3 className="coverage-overall__headline">{OVERALL_HEADLINE[overall.status]}</h3>
            <p className="muted">{OVERALL_EXPLANATION[overall.status]}</p>
          </div>
          <StatusBadge tone={term.tone}>{term.label}</StatusBadge>
        </div>

        <div className="coverage-overall__metrics">
          <Metric label={METRIC_LABELS.coverage} help={METRIC_HELP.coverage} value={overall.score} />
          <Metric
            label={METRIC_LABELS.confidence}
            help={METRIC_HELP.confidence}
            value={overall.confidence}
          />
        </div>

        <p className="faint">{OVERALL_IS_WEAKEST_REGION}</p>

        {/* Implementation detail, not product language. The profile name and
            version are useful when debugging a verdict and meaningless to
            someone photographing a head, so they live in a details element
            rather than beside the score. */}
        <details className="coverage-overall__details">
          <summary className="faint">Analiz ayrıntıları</summary>
          <p className="faint">
            Analiz profili: {coverage.coverage_profile_name} · sürüm{" "}
            {coverage.coverage_profile_version}
            <br />
            Kapsama sürümü {coverage.version} · {formatDateTime(coverage.created_at)}
          </p>
        </details>
      </section>

      <section className="card stack">
        <h3>Bu değerlendirme neyi kapsıyor</h3>
        <DataList
          items={[
            // Three rows, three different facts, and they used to read as a
            // contradiction: "Ölçülmeyen bölgeler: Yok" sat directly above
            // "Hiç ölçülmeyen alanlar: Kafanın üstü, ...". Both were true and
            // neither said what it was about.
            //
            // A *bölge* is one of the five the coverage model defines and can
            // evaluate. An *alan* is anatomy the model does not describe at
            // all - there is no measurement of it to be missing, so it can
            // never move into the row above by capturing more.
            {
              term: "Değerlendirilen bölgeler",
              value:
                measured.length > 0 ? (
                  <RegionNames regions={measured} />
                ) : (
                  <span className="muted">Değerlendirilebilen bölge yok</span>
                ),
            },
            {
              term: "Değerlendirilemeyen bölgeler",
              value:
                notMeasured.length > 0 ? (
                  <RegionNames regions={notMeasured} />
                ) : (
                  // The count, not "Yok": it answers the question the row asks
                  // without the reader having to notice that a list is empty.
                  <span className="muted">
                    Yok — {measured.length} / {measured.length + notMeasured.length} bölge
                    değerlendirildi
                  </span>
                ),
            },
            ...(overall.unmeasured_areas && overall.unmeasured_areas.length > 0
              ? [
                  {
                    term: "Bu sürümde değerlendirme kapsamı dışında",
                    value: (
                      <span>
                        {overall.unmeasured_areas.map(unmeasuredAreaLabel).join(", ")}
                      </span>
                    ),
                  },
                ]
              : []),
            {
              term: "Çekim oturumları",
              value: `${coverage.session_summary.analyzed_sessions} analiz edildi · ${coverage.session_summary.unanalyzed_sessions} analiz edilmedi · toplam ${coverage.session_summary.total_sessions}`,
            },
          ]}
        />
        {coverage.session_summary.unanalyzed_sessions > 0 && (
          <Callout tone="caution">
            {coverage.session_summary.unanalyzed_sessions} çekim oturumu analiz edilmediği
            için bu sonuca dahil edilmedi. <Link to="../cekimler">Çekimler</Link>{" "}
            sekmesinden analizlerini çalıştırabilirsiniz.
          </Callout>
        )}
      </section>

      {needsMore.length > 0 && (
        <Callout tone="caution" title="Ek çekim gerekiyor">
          <ul style={{ margin: "0 0 var(--sp-2)", paddingInlineStart: "1.1rem" }}>
            {needsMore.map((region) => (
              <li key={region}>
                {COVERAGE_STATUS_TERMS[regions[region]!.status].label} —{" "}
                <RegionNames regions={[region]} />
              </li>
            ))}
          </ul>
          <Link className="btn btn--primary" to={paths.newCorrectionSession(projectId)}>
            Düzeltme çekimi ekle
          </Link>
        </Callout>
      )}

      <section className="stack">
        <h3>Bölgeler</h3>
        <div className="regiongrid">
          {COVERAGE_REGIONS.map((region) => (
            <RegionCard
              key={region}
              region={region}
              data={regions[region] ?? null}
              change={changes[region] ?? null}
            />
          ))}
        </div>
      </section>

      <CoverageIssues issues={coverage.issues ?? []} />
    </div>
  );
}

function Metric({ label, help, value }: { label: string; help: string; value: number }) {
  return (
    <div className="coverage-metric">
      <span className="coverage-metric__label" title={help}>
        {label}
      </span>
      <span className="coverage-metric__value">{formatScore(value)}</span>
      <span className="faint">{help}</span>
    </div>
  );
}

/** Region names, always translated - the wire values never reach the DOM. */
function RegionNames({ regions }: { regions: readonly CoverageRegion[] }) {
  return <span>{regions.map((region) => COVERAGE_REGION_LABELS[region]).join(", ")}</span>;
}
